<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { cva } from 'class-variance-authority'
import { Status, StatusType } from '@injectivelabs/utils'
import type { RouteLocationRaw } from '#vue-router'
import type { VariantProps } from 'class-variance-authority'

const buttonClasses = cva(
  [
    'text-center',
    'rounded-sm',
    'bg-neutral-900',
    'text-neutral-900',
    'font-medium',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed'
  ],
  {
    variants: {
      intent: {
        primary: 'bg-primary hover:bg-primary/60 text-white',
        'primary-outline':
          'border bg-transparent border-primary hover:opacity-60 text-primary',
        'light-blue-outline':
          'text-midnight-50 border border-midnight-50  bg-transparent hover:opacity-60',
        'dark-uiGray-outline':
          'text-uiPrimary-100 border border-neutral-100 bg-transparent hover:text-purple-400 hover:border-purple-400',
        'black-outline': 'text-black border border-black bg-transparent'
      },
      size: {
        xxs: 'p-1 text-xs',
        xs: 'py-1 px-3 text-xs',
        sm: 'p-2 text-xs',
        md: 'px-6 py-1.5 text-sm leading-5',
        lg: 'px-12 py-2.5 text-base leading-5'
      }
    }
  }
)

type ButtonProps = VariantProps<typeof buttonClasses>

const props = withDefaults(
  defineProps<{
    status?: Status
    external?: boolean
    isLoading?: boolean
    isDisabled?: boolean
    to?: RouteLocationRaw
    size?: ButtonProps['size']
    intent?: ButtonProps['intent']
    class?: string | string[] | Record<string, boolean>
  }>(),
  {
    intent: 'primary',
    size: 'md',
    class: '',
    status: () => new Status(StatusType.Idle),
    to: undefined,
    external: false
  }
)

const component = computed(() =>
  props.to ? resolveComponent('NuxtLink') : 'button'
)
</script>

<template>
  <component
    :is="component"
    :disabled="props.isDisabled || status.isLoading()"
    :class="twMerge(buttonClasses({ intent, size, class: props.class }))"
    :to="props.to"
    :external="props.external"
    :target="props.external ? '_blank' : undefined"
  >
    <div
      v-if="status.isLoading() || isLoading"
      class="w-4 h-4 rounded-full border border-current border-l-transparent animate-spin mr-2"
    />
    <slot v-else />
  </component>
</template>
